/**
 * usePlanProgress
 *
 * Derives step completion status from real data signals.
 * Uses real hooks where available, graceful fallbacks where not.
 */

import { useMemo } from "react";
import type {
  JourneyKey,
  PlanStep,
  StepStatus,
  SubstepStatus,
  Substep,
} from "../plan.types";
import { getJourneyStepCompletionId } from "../completion";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import type { Document } from "@src/packages/documents/hooks/useDocuments";
import { useTestResults } from "@src/packages/test-results/hooks/useTestResults";
import { useCoverage } from "@src/packages/coverage/hooks/useCoverage";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import type { Cycle } from "@src/packages/cycles/hooks/useCycles";
import {
  calculateBasicInfoCompletion,
  type Patient,
} from "@src/packages/medical-history/basic-info/calculateBasicInfoCompletion";
import type { TestResult } from "@src/packages/test-results/hooks/useTestResults";
import { useManualStepCompletion } from "./useManualStepCompletion";

/** Narrow an unknown value to an indexable record for safe dot-path traversal. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Walk a dot-path off an unknown root without `any`. Returns undefined if any hop is missing. */
function readByPath(root: unknown, path: string): unknown {
  let value: unknown = root;
  for (const key of path.split(".")) {
    if (!isRecord(value)) return undefined;
    value = value[key];
  }
  return value;
}

/**
 * Documents are categorised through `tags` (e.g. "coverage_benefits",
 * "legal_consent") and their human name — there is no `category` field on the
 * API document. Match a document against a free-text token using both.
 */
function documentMatches(doc: Document, token: string): boolean {
  const needle = token.toLowerCase();
  if (doc.tags.some((tag) => tag.toLowerCase().includes(needle))) return true;
  return doc.name.toLowerCase().includes(needle);
}

export type StepProgress = {
  stepId: string;
  status: StepStatus;
  substeps: SubstepProgress[];
  completionPercent: number;
};

export type SubstepProgress = {
  substepId: string;
  status: SubstepStatus;
  scheduledDate?: string;
  expectedResultDate?: string;
  resultData?: unknown;
  interpretation?: string;
};

/**
 * Check if a completion signal is satisfied based on available data
 */
function evaluateCompletionSignal(
  step: PlanStep,
  patient: Patient | null | undefined,
  documents: Document[],
  medicalHistoryPercent: number,
  pastCycles?: Cycle[],
  testResults?: TestResult[],
  manuallyCompletedIds?: string[],
  manualStepId = step.id,
): StepStatus {
  const signal = step.completionSignal;

  switch (signal.type) {
    case "manual": {
      if (manuallyCompletedIds?.includes(manualStepId)) return "complete";

      // Auto-complete Learn steps for returning users with past cycles
      if (step.id === "learn" && pastCycles && pastCycles.length > 0) {
        return "complete";
      }

      return "not-started";
    }

    case "data-exists": {
      // Check if the specified data path exists on patient
      const value = readByPath(patient, signal.check);
      if (value !== undefined && value !== null) return "complete";
      return "not-started";
    }

    case "test-result-exists": {
      // Check if any of the specified test substep IDs have matching results
      if (!testResults || testResults.length === 0) return "not-started";
      const matched = signal.testIds.filter((id) => {
        const matcher = SUBSTEP_TEST_MAP[id];
        return matcher ? testResults.some(matcher) : false;
      });
      if (matched.length >= signal.testIds.length) return "complete";
      if (matched.length > 0) return "in-progress";
      return "not-started";
    }

    case "completion-percent": {
      // Use medical history completion as proxy for prep readiness
      if (signal.field === "prepReadiness") {
        if (medicalHistoryPercent >= signal.threshold) return "complete";
        if (medicalHistoryPercent > 0) return "in-progress";
      }
      return "not-started";
    }

    case "document-uploaded": {
      const hasDoc = documents.some((doc) =>
        documentMatches(doc, signal.category),
      );
      if (hasDoc) return "complete";
      return "not-started";
    }

    case "field-set": {
      // Support dot-path fields (e.g., "fertilityIntent.clinicId")
      const fieldValue = readByPath(patient, signal.field);
      if (fieldValue !== undefined && fieldValue !== null) return "complete";
      return "not-started";
    }

    case "all-substeps": {
      if (!step.substeps || step.substeps.length === 0) return "not-started";
      const allDone = step.substeps.every(
        (s) => s.status === "complete" || s.status === "results-in",
      );
      if (allDone) return "complete";
      const anyStarted = step.substeps.some((s) => s.status !== "not-started");
      if (anyStarted) return "in-progress";
      return "not-started";
    }

    case "external": {
      // External signals are set by the backend
      return "not-started";
    }

    default:
      return "not-started";
  }
}

/**
 * Map substep IDs to test result matching rules
 */
const SUBSTEP_TEST_MAP: Record<string, (t: TestResult) => boolean> = {
  amh: (t) =>
    (t.type === "blood" &&
      t.bloodTestCategory === "hormonal" &&
      (t.name?.toLowerCase().includes("amh") ?? false)) ||
    (t.name?.toLowerCase().includes("anti-müllerian") ?? false),
  "fsh-estradiol": (t) =>
    t.type === "blood" &&
    t.bloodTestCategory === "hormonal" &&
    ((t.name?.toLowerCase().includes("fsh") ?? false) ||
      (t.name?.toLowerCase().includes("estradiol") ?? false)),
  afc: (t) =>
    t.type === "imaging" &&
    (t.imagingTestCategory === "transvaginal_ultrasound" ||
      (t.name?.toLowerCase().includes("afc") ?? false) ||
      (t.name?.toLowerCase().includes("antral follicle") ?? false)),
  hsg: (t) =>
    t.type === "imaging" &&
    (t.imagingTestCategory === "hysterosonography" ||
      (t.name?.toLowerCase().includes("hsg") ?? false)),
  "infectious-disease": (t) =>
    t.type === "blood" && t.bloodTestCategory === "std",
  "partner-sa": (t) => t.type === "semenFluidAnalysis",
};

type SubstepContext = {
  testResults: TestResult[];
  documents: Document[];
  hasInsurance: boolean;
  medicalHistoryPercent: number;
};

/**
 * Derive substep status from available data
 */
function deriveSubstepStatus(
  substep: Substep,
  ctx: SubstepContext,
): SubstepProgress {
  const derived = deriveStatusFromData(substep.id, ctx);

  return {
    substepId: substep.id,
    status: derived ?? substep.status,
    scheduledDate: substep.scheduledDate,
    expectedResultDate: substep.expectedResultDate,
    resultData: substep.resultData,
    interpretation: substep.interpretation,
  };
}

function deriveStatusFromData(
  substepId: string,
  ctx: SubstepContext,
): SubstepStatus | null {
  // Test-based substeps
  const testMatcher = SUBSTEP_TEST_MAP[substepId];
  if (testMatcher) {
    const match = ctx.testResults.find(testMatcher);
    if (match) {
      return match.value || match.doctorValue
        ? "results-in"
        : "done-awaiting-results";
    }
    return null;
  }

  // Document/coverage substeps
  if (substepId === "insurance-card") {
    if (ctx.hasInsurance) return "complete";
    const hasDoc = ctx.documents.some(
      (d) => documentMatches(d, "insurance") || documentMatches(d, "coverage"),
    );
    if (hasDoc) return "complete";
    return null;
  }

  if (substepId === "consent-forms") {
    const hasConsent = ctx.documents.some(
      (d) =>
        documentMatches(d, "consent") || documentMatches(d, "legal_consent"),
    );
    if (hasConsent) return "complete";
    return null;
  }

  // Medical history substep
  if (
    substepId === "medical-history" ||
    substepId === "partner-medical-history"
  ) {
    if (ctx.medicalHistoryPercent >= 80) return "complete";
    if (ctx.medicalHistoryPercent > 0) return "scheduled";
    return null;
  }

  // No data signal available — keep static status from journey
  return null;
}

/**
 * Hook: derive plan progress from real data
 */
export function usePlanProgress(steps: PlanStep[], journeyKey?: JourneyKey) {
  const { data: patient } = usePatient();
  const { data: documentsResponse } = useDocuments();
  const { data: testResults = [] } = useTestResults();
  const { hasInsurance } = useCoverage();
  const { data: cycles } = useCycles();
  const { completed: manuallyCompletedIds } = useManualStepCompletion();

  // useDocuments resolves to Document[] directly (its queryFn returns
  // response.data), so the list is read straight off the query result.
  const documents = useMemo<Document[]>(
    () => documentsResponse ?? [],
    [documentsResponse],
  );

  const medicalHistoryPercent = useMemo(() => {
    if (!patient) return 0;
    try {
      return calculateBasicInfoCompletion(patient).percent;
    } catch {
      return 0;
    }
  }, [patient]);

  const progress = useMemo((): StepProgress[] => {
    const hasOpenCycle = (cycles ?? []).some((c) => c.status === "open");
    let foundActive = false;

    return steps.map((step) => {
      // Past closed cycles — used to auto-advance Learn steps
      const closedCycles = (cycles ?? []).filter((c) => c.status === "closed");

      const manualStepId = journeyKey
        ? getJourneyStepCompletionId(journeyKey, step.id)
        : step.id;
      const status = evaluateCompletionSignal(
        step,
        patient,
        documents,
        medicalHistoryPercent,
        closedCycles,
        testResults,
        manuallyCompletedIds,
        manualStepId,
      );

      const substeps = (step.substeps ?? []).map((sub) =>
        deriveSubstepStatus(sub, {
          testResults,
          documents,
          hasInsurance,
          medicalHistoryPercent,
        }),
      );

      const completedCount = substeps.filter(
        (s) => s.status === "complete" || s.status === "results-in",
      ).length;
      const completionPercent =
        substeps.length > 0
          ? Math.round((completedCount / substeps.length) * 100)
          : status === "complete"
            ? 100
            : 0;

      // When a cycle is open, the treatment step is the active one.
      // Earlier non-complete steps stay "not-started" so they don't claim the active slot.
      if (hasOpenCycle) {
        if (step.id === "treatment") {
          foundActive = true;
          return {
            stepId: step.id,
            status: "in-progress",
            substeps,
            completionPercent,
          };
        }
        return {
          stepId: step.id,
          status: status === "complete" ? "complete" : "not-started",
          substeps,
          completionPercent,
        };
      }

      // Determine effective status: first non-complete step is "in-progress"
      let effectiveStatus = status;
      if (status !== "complete" && !foundActive) {
        effectiveStatus = "in-progress";
        foundActive = true;
      } else if (status !== "complete" && foundActive) {
        effectiveStatus = "not-started";
      }

      return {
        stepId: step.id,
        status: effectiveStatus,
        substeps,
        completionPercent,
      };
    });
  }, [
    steps,
    patient,
    documents,
    medicalHistoryPercent,
    testResults,
    hasInsurance,
    cycles,
    manuallyCompletedIds,
    journeyKey,
  ]);

  return { progress, patient };
}
